/* =====================================================================
 * @auw/plugins — Built-in plugin descriptors.
 *
 * These 13 integrations ship with every askyour.ai build. They replace the
 * six separate inline tables that previously lived in
 * `components/plugin/PluginModal.tsx` (`PLUGIN_LABEL_MAP`,
 * `PLUGIN_META`, `PLUGIN_CATALOG_INFO`, `PLUGIN_SCOPE_NOTICES`,
 * `MCP_CONNECTOR_INFO`, `PLUGIN_TOOLS`), collapsing them into a single
 * source of truth that enterprise tenants can restrict, reorder, or
 * override via `tenant.plugins`.
 *
 * `developer` field convention
 *   When the vendor runs the MCP/OAuth server themselves (Notion),
 *   we credit them directly. For in-house connectors wrapping a
 *   vendor's public API (Slack OAuth, Gmail, Atlassian OAuth, …) we
 *   historically credited `askyour.ai` — the current B2C fallback.
 *   Enterprise tenants can override this per-plugin to show their
 *   own brand instead.
 * ===================================================================== */

import type { PluginDescriptor, PluginId } from './types';

/** Default attribution for in-house OAuth wrappers. B2B tenants can
 *  override via `tenant.plugins.overrides['<id>'].catalog.developer`. */
const DEFAULT_INHOUSE_DEVELOPER = 'askyour.ai';

/** Ordered roster of descriptors. `resolvePluginCatalog()` falls back
 *  to this order when the tenant manifest doesn't specify an explicit
 *  `plugins.order` array, preserving the legacy B2C ordering. */
export const BUILTIN_PLUGINS: readonly PluginDescriptor[] = [
  {
    id: 'slack',
    label: 'Slack',
    category: 'businessCommunication',
    meta: {
      logoSrc: '/logo/slack.png',
      logoAlt: 'Slack',
      accentColor: '#4A154B',
      accentBackground: 'rgba(74, 21, 75, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.businessCommunication',
      featuresKey: 'pluginDetail.features.slack',
      descriptionKey: 'pluginDetail.descriptions.slack',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://slack.com',
      privacyPolicyUrl: 'https://slack.com/trust/privacy/privacy-policy',
    },
    tools: [
      { name: 'list_channels', descriptionKey: 'pluginDetail.tools.slack.list_channels' },
      { name: 'post_message', descriptionKey: 'pluginDetail.tools.slack.post_message' },
      { name: 'reply_to_thread', descriptionKey: 'pluginDetail.tools.slack.reply_to_thread' },
      { name: 'add_reaction', descriptionKey: 'pluginDetail.tools.slack.add_reaction' },
      { name: 'get_channel_history', descriptionKey: 'pluginDetail.tools.slack.get_channel_history' },
      { name: 'get_thread_replies', descriptionKey: 'pluginDetail.tools.slack.get_thread_replies' },
      { name: 'get_channel_context', descriptionKey: 'pluginDetail.tools.slack.get_channel_context' },
      { name: 'get_users', descriptionKey: 'pluginDetail.tools.slack.get_users' },
      { name: 'get_user_profile', descriptionKey: 'pluginDetail.tools.slack.get_user_profile' },
      { name: 'join_channel', descriptionKey: 'pluginDetail.tools.slack.join_channel' },
    ],
  },
  {
    id: 'notion',
    label: 'Notion',
    category: 'documentCollaboration',
    meta: {
      logoSrc: '/logo/notion.png',
      logoAlt: 'Notion',
      accentColor: '#111111',
      accentBackground: 'rgba(17, 17, 17, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.documentCollaboration',
      featuresKey: 'pluginDetail.features.notion',
      descriptionKey: 'pluginDetail.descriptions.notion',
      developer: 'Notion',
      websiteUrl: 'https://www.notion.so',
      privacyPolicyUrl: 'https://www.notion.so/privacy',
    },
    tools: [
      { name: 'notion-search', descriptionKey: '' },
      { name: 'notion-fetch', descriptionKey: '' },
      { name: 'notion-create-pages', descriptionKey: '' },
      { name: 'notion-update-page', descriptionKey: '' },
      { name: 'notion-move-pages', descriptionKey: '' },
      { name: 'notion-duplicate-page', descriptionKey: '' },
      { name: 'notion-create-database', descriptionKey: '' },
      { name: 'notion-update-data-source', descriptionKey: '' },
      { name: 'notion-create-comment', descriptionKey: '' },
      { name: 'notion-get-comments', descriptionKey: '' },
      { name: 'notion-get-teams', descriptionKey: '' },
      { name: 'notion-get-users', descriptionKey: '' },
      { name: 'notion-create-view', descriptionKey: '' },
      { name: 'notion-update-view', descriptionKey: '' },
    ],
    mcp: {
      version: '1.0.0',
      author: 'Notion',
      authorUrl: 'https://www.notion.so',
      connectorUrl: 'https://mcp.notion.com/mcp',
      docsUrl: 'https://developers.notion.com/guides/mcp/mcp-supported-tools',
      defaultToolsVisible: 8,
    },
  },
  {
    id: 'atlassian',
    label: 'Atlassian',
    category: 'projectManagement',
    meta: {
      logoSrc: '/logo/atlassian.png',
      logoAlt: 'Atlassian',
      accentColor: '#0052CC',
      accentBackground: 'rgba(0, 82, 204, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.projectManagement',
      featuresKey: 'pluginDetail.features.atlassian',
      descriptionKey: 'pluginDetail.descriptions.atlassian',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://www.atlassian.com',
      privacyPolicyUrl: 'https://www.atlassian.com/legal/privacy-policy',
    },
    tools: [
      { name: 'jira_search_issues', descriptionKey: 'pluginDetail.tools.atlassian.jira_search_issues' },
      { name: 'jira_get_issue', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_issue' },
      { name: 'jira_get_projects', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_projects' },
      { name: 'jira_list_issue_types', descriptionKey: 'pluginDetail.tools.atlassian.jira_list_issue_types' },
      { name: 'jira_get_issue_fields', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_issue_fields' },
      { name: 'jira_get_issue_transitions', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_issue_transitions' },
      { name: 'jira_search_users', descriptionKey: 'pluginDetail.tools.atlassian.jira_search_users' },
      { name: 'jira_get_current_user', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_current_user' },
      { name: 'jira_list_accessible_resources', descriptionKey: 'pluginDetail.tools.atlassian.jira_list_accessible_resources' },
      { name: 'jira_get_issue_link_types', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_issue_link_types' },
      { name: 'jira_get_remote_links', descriptionKey: 'pluginDetail.tools.atlassian.jira_get_remote_links' },
      { name: 'jira_create_issue', descriptionKey: 'pluginDetail.tools.atlassian.jira_create_issue' },
      { name: 'jira_update_issue', descriptionKey: 'pluginDetail.tools.atlassian.jira_update_issue' },
      { name: 'jira_add_comment', descriptionKey: 'pluginDetail.tools.atlassian.jira_add_comment' },
      { name: 'jira_transition_issue', descriptionKey: 'pluginDetail.tools.atlassian.jira_transition_issue' },
      { name: 'jira_create_issue_link', descriptionKey: 'pluginDetail.tools.atlassian.jira_create_issue_link' },
      { name: 'confluence_search', descriptionKey: 'pluginDetail.tools.atlassian.confluence_search' },
      { name: 'confluence_list_recent_pages', descriptionKey: 'pluginDetail.tools.atlassian.confluence_list_recent_pages' },
      { name: 'confluence_get_page', descriptionKey: 'pluginDetail.tools.atlassian.confluence_get_page' },
      { name: 'confluence_get_spaces', descriptionKey: 'pluginDetail.tools.atlassian.confluence_get_spaces' },
      { name: 'confluence_get_page_children', descriptionKey: 'pluginDetail.tools.atlassian.confluence_get_page_children' },
      { name: 'confluence_get_page_comments', descriptionKey: 'pluginDetail.tools.atlassian.confluence_get_page_comments' },
      { name: 'confluence_get_comment_replies', descriptionKey: 'pluginDetail.tools.atlassian.confluence_get_comment_replies' },
      { name: 'confluence_list_inline_comments', descriptionKey: 'pluginDetail.tools.atlassian.confluence_list_inline_comments' },
      { name: 'confluence_create_page', descriptionKey: 'pluginDetail.tools.atlassian.confluence_create_page' },
      { name: 'confluence_update_page', descriptionKey: 'pluginDetail.tools.atlassian.confluence_update_page' },
      { name: 'confluence_add_comment', descriptionKey: 'pluginDetail.tools.atlassian.confluence_add_comment' },
      { name: 'confluence_create_inline_comment', descriptionKey: 'pluginDetail.tools.atlassian.confluence_create_inline_comment' },
    ],
  },
  {
    id: 'gmail',
    label: 'Gmail',
    category: 'email',
    meta: {
      logoSrc: '/logo/google_gmail.webp',
      logoAlt: 'Gmail',
      accentColor: '#5F6368',
      accentBackground: 'rgba(128, 128, 128, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.email',
      featuresKey: 'pluginDetail.features.gmail',
      descriptionKey: 'pluginDetail.descriptions.gmail',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://workspace.google.com/products/gmail/',
      privacyPolicyUrl: 'https://policies.google.com/privacy',
    },
    tools: [
      { name: 'search_emails', descriptionKey: 'pluginDetail.tools.gmail.search_emails' },
      { name: 'search_threads', descriptionKey: 'pluginDetail.tools.gmail.search_threads' },
      { name: 'get_email', descriptionKey: 'pluginDetail.tools.gmail.get_email' },
      { name: 'resolve_recipients', descriptionKey: 'pluginDetail.tools.gmail.resolve_recipients' },
      { name: 'send_email', descriptionKey: 'pluginDetail.tools.gmail.send_email' },
      { name: 'reply_to_email', descriptionKey: 'pluginDetail.tools.gmail.reply_to_email' },
      { name: 'get_labels', descriptionKey: 'pluginDetail.tools.gmail.get_labels' },
      { name: 'modify_labels', descriptionKey: 'pluginDetail.tools.gmail.modify_labels' },
      { name: 'get_thread', descriptionKey: 'pluginDetail.tools.gmail.get_thread' },
      { name: 'get_profile', descriptionKey: 'pluginDetail.tools.gmail.get_profile' },
    ],
    scopeNotice: {
      titleKey: 'pluginDetail.scopeNotice.gmailTitle',
      descriptionKey: 'pluginDetail.scopeNotice.gmailDescription',
    },
  },
  {
    id: 'google_drive',
    label: 'Google Drive',
    category: 'cloudStorage',
    meta: {
      logoSrc: '/logo/google_drive.png',
      logoAlt: 'Google Drive',
      accentColor: '#4285F4',
      accentBackground: 'rgba(66, 133, 244, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.cloudStorage',
      featuresKey: 'pluginDetail.features.googleDrive',
      descriptionKey: 'pluginDetail.descriptions.googleDrive',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://workspace.google.com/products/drive/',
      privacyPolicyUrl: 'https://policies.google.com/privacy',
    },
    tools: [
      { name: 'search_files', descriptionKey: 'pluginDetail.tools.google_drive.search_files' },
      { name: 'get_file_info', descriptionKey: 'pluginDetail.tools.google_drive.get_file_info' },
      { name: 'read_file', descriptionKey: 'pluginDetail.tools.google_drive.read_file' },
      { name: 'list_folder', descriptionKey: 'pluginDetail.tools.google_drive.list_folder' },
      { name: 'create_file', descriptionKey: 'pluginDetail.tools.google_drive.create_file' },
      { name: 'upload_file', descriptionKey: 'pluginDetail.tools.google_drive.upload_file' },
      { name: 'share_file', descriptionKey: 'pluginDetail.tools.google_drive.share_file' },
      { name: 'move_file', descriptionKey: 'pluginDetail.tools.google_drive.move_file' },
      { name: 'get_about', descriptionKey: 'pluginDetail.tools.google_drive.get_about' },
    ],
  },
  {
    id: 'google_calendar',
    label: 'Google Calendar',
    category: 'schedule',
    meta: {
      logoSrc: '/logo/google_calendar.webp',
      logoAlt: 'Google Calendar',
      accentColor: '#34A853',
      accentBackground: 'rgba(52, 168, 83, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.schedule',
      featuresKey: 'pluginDetail.features.googleCalendar',
      descriptionKey: 'pluginDetail.descriptions.googleCalendar',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://workspace.google.com/products/calendar/',
      privacyPolicyUrl: 'https://policies.google.com/privacy',
    },
    tools: [
      { name: 'list_events', descriptionKey: 'pluginDetail.tools.google_calendar.list_events' },
      { name: 'get_event', descriptionKey: 'pluginDetail.tools.google_calendar.get_event' },
      { name: 'create_event', descriptionKey: 'pluginDetail.tools.google_calendar.create_event' },
      { name: 'create_meet_event', descriptionKey: 'pluginDetail.tools.google_calendar.create_meet_event' },
      { name: 'update_event', descriptionKey: 'pluginDetail.tools.google_calendar.update_event' },
      { name: 'add_meet_link', descriptionKey: 'pluginDetail.tools.google_calendar.add_meet_link' },
      { name: 'delete_event', descriptionKey: 'pluginDetail.tools.google_calendar.delete_event' },
      { name: 'quick_add', descriptionKey: 'pluginDetail.tools.google_calendar.quick_add' },
      { name: 'list_calendars', descriptionKey: 'pluginDetail.tools.google_calendar.list_calendars' },
      { name: 'today', descriptionKey: 'pluginDetail.tools.google_calendar.today' },
      { name: 'respond_event', descriptionKey: 'pluginDetail.tools.google_calendar.respond_event' },
      { name: 'search_person_events', descriptionKey: 'pluginDetail.tools.google_calendar.search_person_events' },
      { name: 'check_availability', descriptionKey: 'pluginDetail.tools.google_calendar.check_availability' },
      { name: 'search_across_calendars', descriptionKey: 'pluginDetail.tools.google_calendar.search_across_calendars' },
      { name: 'search_directory_people', descriptionKey: 'pluginDetail.tools.google_calendar.search_directory_people' },
      { name: 'view_person_calendar', descriptionKey: 'pluginDetail.tools.google_calendar.view_person_calendar' },
    ],
    scopeNotice: {
      titleKey: 'pluginDetail.scopeNotice.googleCalendarTitle',
      descriptionKey: 'pluginDetail.scopeNotice.googleCalendarDescription',
    },
  },
  {
    id: 'discord',
    label: 'Discord',
    category: 'community',
    meta: {
      logoSrc: '/logo/discord.png',
      logoAlt: 'Discord',
      accentColor: '#7289DA',
      accentBackground: 'rgba(114, 137, 218, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.community',
      featuresKey: 'pluginDetail.features.discord',
      descriptionKey: 'pluginDetail.descriptions.discord',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://discord.com',
      privacyPolicyUrl: 'https://discord.com/privacy',
    },
    tools: [
      { name: 'list_guilds', descriptionKey: 'pluginDetail.tools.discord.list_guilds' },
      { name: 'list_channels', descriptionKey: 'pluginDetail.tools.discord.list_channels' },
      { name: 'get_channel_history', descriptionKey: 'pluginDetail.tools.discord.get_channel_history' },
      { name: 'send_message', descriptionKey: 'pluginDetail.tools.discord.send_message' },
      { name: 'get_user_info', descriptionKey: 'pluginDetail.tools.discord.get_user_info' },
      { name: 'list_members', descriptionKey: 'pluginDetail.tools.discord.list_members' },
      { name: 'search_messages', descriptionKey: 'pluginDetail.tools.discord.search_messages' },
      { name: 'add_reaction', descriptionKey: 'pluginDetail.tools.discord.add_reaction' },
      { name: 'reply_to_message', descriptionKey: 'pluginDetail.tools.discord.reply_to_message' },
    ],
  },
  {
    id: 'github',
    label: 'GitHub',
    category: 'developerTools',
    meta: {
      logoSrc: '/logo/github.png',
      logoAlt: 'GitHub',
      accentColor: '#24292E',
      accentBackground: 'rgba(36, 41, 46, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.developerTools',
      featuresKey: 'pluginDetail.features.github',
      descriptionKey: 'pluginDetail.descriptions.github',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://github.com',
      privacyPolicyUrl:
        'https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement',
    },
    tools: [
      { name: 'get_profile', descriptionKey: 'pluginDetail.tools.github.get_profile' },
      { name: 'list_my_organizations', descriptionKey: 'pluginDetail.tools.github.list_my_organizations' },
      { name: 'resolve_owner_candidates', descriptionKey: 'pluginDetail.tools.github.resolve_owner_candidates' },
      { name: 'search_repositories', descriptionKey: 'pluginDetail.tools.github.search_repositories' },
      { name: 'list_repositories', descriptionKey: 'pluginDetail.tools.github.list_repositories' },
      { name: 'get_organization', descriptionKey: 'pluginDetail.tools.github.get_organization' },
      { name: 'list_owner_repositories', descriptionKey: 'pluginDetail.tools.github.list_owner_repositories' },
      { name: 'find_owner_repositories', descriptionKey: 'pluginDetail.tools.github.find_owner_repositories' },
      { name: 'get_repository', descriptionKey: 'pluginDetail.tools.github.get_repository' },
      { name: 'list_issues', descriptionKey: 'pluginDetail.tools.github.list_issues' },
      { name: 'get_issue', descriptionKey: 'pluginDetail.tools.github.get_issue' },
      { name: 'create_issue', descriptionKey: 'pluginDetail.tools.github.create_issue' },
      { name: 'add_issue_comment', descriptionKey: 'pluginDetail.tools.github.add_issue_comment' },
      { name: 'list_pull_requests', descriptionKey: 'pluginDetail.tools.github.list_pull_requests' },
      { name: 'get_pull_request', descriptionKey: 'pluginDetail.tools.github.get_pull_request' },
    ],
  },
  {
    id: 'microsoft_teams',
    label: 'Microsoft Teams',
    category: 'businessCommunication',
    meta: {
      logoSrc: '/logo/teams.webp',
      logoAlt: 'Microsoft Teams',
      accentColor: '#464775',
      accentBackground: 'rgba(70, 71, 117, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.businessCommunication',
      featuresKey: 'pluginDetail.features.microsoftTeams',
      descriptionKey: 'pluginDetail.descriptions.microsoftTeams',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://www.microsoft.com/en-us/microsoft-teams/',
      privacyPolicyUrl: 'https://privacy.microsoft.com/privacystatement',
    },
    tools: [
      { name: 'list_joined_teams', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_joined_teams' },
      { name: 'list_channels', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_channels' },
      { name: 'list_workspace_channels', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_workspace_channels' },
      { name: 'get_team', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_team' },
      { name: 'get_channel', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_channel' },
      { name: 'list_channel_members', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_channel_members' },
      { name: 'create_channel', descriptionKey: 'pluginDetail.tools.microsoft_teams.create_channel' },
      { name: 'update_channel', descriptionKey: 'pluginDetail.tools.microsoft_teams.update_channel' },
      { name: 'add_channel_member', descriptionKey: 'pluginDetail.tools.microsoft_teams.add_channel_member' },
      { name: 'update_channel_member', descriptionKey: 'pluginDetail.tools.microsoft_teams.update_channel_member' },
      { name: 'get_channel_messages', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_channel_messages' },
      { name: 'send_channel_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.send_channel_message' },
      { name: 'reply_to_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.reply_to_message' },
      { name: 'list_chats', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_chats' },
      { name: 'get_chat', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_chat' },
      { name: 'list_chat_members', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_chat_members' },
      { name: 'create_chat', descriptionKey: 'pluginDetail.tools.microsoft_teams.create_chat' },
      { name: 'update_chat', descriptionKey: 'pluginDetail.tools.microsoft_teams.update_chat' },
      { name: 'delete_chat', descriptionKey: 'pluginDetail.tools.microsoft_teams.delete_chat' },
      { name: 'add_chat_member', descriptionKey: 'pluginDetail.tools.microsoft_teams.add_chat_member' },
      { name: 'get_chat_messages', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_chat_messages' },
      { name: 'get_chat_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_chat_message' },
      { name: 'send_chat_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.send_chat_message' },
      { name: 'update_chat_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.update_chat_message' },
      { name: 'delete_chat_message', descriptionKey: 'pluginDetail.tools.microsoft_teams.delete_chat_message' },
      { name: 'list_message_attachments', descriptionKey: 'pluginDetail.tools.microsoft_teams.list_message_attachments' },
      { name: 'download_attachment', descriptionKey: 'pluginDetail.tools.microsoft_teams.download_attachment' },
      { name: 'get_my_profile', descriptionKey: 'pluginDetail.tools.microsoft_teams.get_my_profile' },
    ],
    scopeNotice: {
      titleKey: 'pluginDetail.scopeNotice.microsoftTeamsTitle',
      descriptionKey: 'pluginDetail.scopeNotice.microsoftTeamsDescription',
    },
  },
  {
    id: 'zoom',
    label: 'Zoom',
    category: 'videoConference',
    meta: {
      logoSrc: '/logo/zoom.webp',
      logoAlt: 'Zoom',
      accentColor: '#2D8CFF',
      accentBackground: 'rgba(45, 140, 255, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.videoConference',
      featuresKey: 'pluginDetail.features.zoom',
      descriptionKey: 'pluginDetail.descriptions.zoom',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl: 'https://zoom.us',
      privacyPolicyUrl: 'https://zoom.com/en/trust/privacy/privacy-statement',
    },
    tools: [
      { name: 'get_my_profile', descriptionKey: 'pluginDetail.tools.zoom.get_my_profile' },
      { name: 'list_upcoming_meetings', descriptionKey: 'pluginDetail.tools.zoom.list_upcoming_meetings' },
      { name: 'get_meeting', descriptionKey: 'pluginDetail.tools.zoom.get_meeting' },
      { name: 'create_meeting', descriptionKey: 'pluginDetail.tools.zoom.create_meeting' },
      { name: 'update_meeting', descriptionKey: 'pluginDetail.tools.zoom.update_meeting' },
      { name: 'delete_meeting', descriptionKey: 'pluginDetail.tools.zoom.delete_meeting' },
    ],
  },
  {
    id: 'outlook',
    label: 'Outlook Mail',
    category: 'email',
    meta: {
      logoSrc: '/logo/outlook.webp',
      logoAlt: 'Outlook Mail',
      accentColor: '#0078D4',
      accentBackground: 'rgba(0, 120, 212, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.email',
      featuresKey: 'pluginDetail.features.outlook',
      descriptionKey: 'pluginDetail.descriptions.outlook',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl:
        'https://www.microsoft.com/en/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook',
      privacyPolicyUrl: 'https://privacy.microsoft.com/privacystatement',
    },
    tools: [
      { name: 'search_messages', descriptionKey: 'pluginDetail.tools.outlook.search_messages' },
      { name: 'get_message', descriptionKey: 'pluginDetail.tools.outlook.get_message' },
      { name: 'list_sent', descriptionKey: 'pluginDetail.tools.outlook.list_sent' },
      { name: 'list_folders', descriptionKey: 'pluginDetail.tools.outlook.list_folders' },
      { name: 'send_mail', descriptionKey: 'pluginDetail.tools.outlook.send_mail' },
      { name: 'create_draft', descriptionKey: 'pluginDetail.tools.outlook.create_draft' },
      { name: 'send_draft', descriptionKey: 'pluginDetail.tools.outlook.send_draft' },
      { name: 'update_message', descriptionKey: 'pluginDetail.tools.outlook.update_message' },
      { name: 'reply', descriptionKey: 'pluginDetail.tools.outlook.reply' },
      { name: 'reply_all', descriptionKey: 'pluginDetail.tools.outlook.reply_all' },
      { name: 'move_email', descriptionKey: 'pluginDetail.tools.outlook.move_email' },
      { name: 'delete_message', descriptionKey: 'pluginDetail.tools.outlook.delete_message' },
      { name: 'list_attachments', descriptionKey: 'pluginDetail.tools.outlook.list_attachments' },
      { name: 'download_attachment', descriptionKey: 'pluginDetail.tools.outlook.download_attachment' },
      { name: 'get_profile', descriptionKey: 'pluginDetail.tools.outlook.get_profile' },
    ],
  },
  {
    id: 'outlook_calendar',
    label: 'Outlook Calendar',
    category: 'schedule',
    meta: {
      logoSrc: '/logo/outlook.webp',
      logoAlt: 'Outlook Calendar',
      accentColor: '#0078D4',
      accentBackground: 'rgba(0, 120, 212, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.schedule',
      featuresKey: 'pluginDetail.features.outlookCalendar',
      descriptionKey: 'pluginDetail.descriptions.outlookCalendar',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl:
        'https://www.microsoft.com/en/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook',
      privacyPolicyUrl: 'https://privacy.microsoft.com/privacystatement',
    },
    tools: [
      { name: 'list_calendars', descriptionKey: 'pluginDetail.tools.outlook_calendar.list_calendars' },
      { name: 'list_events', descriptionKey: 'pluginDetail.tools.outlook_calendar.list_events' },
      { name: 'search_across_calendars', descriptionKey: 'pluginDetail.tools.outlook_calendar.search_across_calendars' },
      { name: 'get_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.get_event' },
      { name: 'get_schedule', descriptionKey: 'pluginDetail.tools.outlook_calendar.get_schedule' },
      { name: 'find_meeting_times', descriptionKey: 'pluginDetail.tools.outlook_calendar.find_meeting_times' },
      { name: 'create_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.create_event' },
      { name: 'update_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.update_event' },
      { name: 'delete_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.delete_event' },
      { name: 'cancel_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.cancel_event' },
      { name: 'accept_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.accept_event' },
      { name: 'decline_event', descriptionKey: 'pluginDetail.tools.outlook_calendar.decline_event' },
    ],
  },
  {
    id: 'onedrive',
    label: 'OneDrive',
    category: 'cloudStorage',
    meta: {
      logoSrc: '/logo/onedrive.png',
      logoAlt: 'OneDrive',
      accentColor: '#0078D4',
      accentBackground: 'rgba(0, 120, 212, 0.08)',
    },
    catalog: {
      categoryKey: 'pluginDetail.categories.cloudStorage',
      featuresKey: 'pluginDetail.features.onedrive',
      descriptionKey: 'pluginDetail.descriptions.onedrive',
      developer: DEFAULT_INHOUSE_DEVELOPER,
      websiteUrl:
        'https://www.microsoft.com/en-us/microsoft-365/onedrive/online-cloud-storage',
      privacyPolicyUrl: 'https://privacy.microsoft.com/privacystatement',
    },
    tools: [
      { name: 'list_files', descriptionKey: 'pluginDetail.tools.onedrive.list_files' },
      { name: 'search_files', descriptionKey: 'pluginDetail.tools.onedrive.search_files' },
      { name: 'get_file_info', descriptionKey: 'pluginDetail.tools.onedrive.get_file_info' },
      { name: 'read_file', descriptionKey: 'pluginDetail.tools.onedrive.read_file' },
      { name: 'upload_file', descriptionKey: 'pluginDetail.tools.onedrive.upload_file' },
      { name: 'create_folder', descriptionKey: 'pluginDetail.tools.onedrive.create_folder' },
      { name: 'move_item', descriptionKey: 'pluginDetail.tools.onedrive.move_item' },
      { name: 'copy_item', descriptionKey: 'pluginDetail.tools.onedrive.copy_item' },
      { name: 'delete_item', descriptionKey: 'pluginDetail.tools.onedrive.delete_item' },
      { name: 'share_item', descriptionKey: 'pluginDetail.tools.onedrive.share_item' },
      { name: 'get_drive_info', descriptionKey: 'pluginDetail.tools.onedrive.get_drive_info' },
    ],
  },

  {
    id: 'samsung_theplan',
    label: '삼성중공업 The PLAN',
    category: 'productivity',
    meta: {
      logoSrc: '',
      logoAlt: 'Samsung The PLAN',
      accentColor: '#0055FF',
      accentBackground: 'rgba(0, 85, 255, 0.08)',
    },
    catalog: {
      categoryKey: null,
      featuresKey: null,
      descriptionKey: 'pluginDetail.descriptions.samsungThePlan',
      developer: '삼성중공업',
      websiteUrl: null,
      privacyPolicyUrl: null,
    },
    tools: [],
    ux: {
      connectionMode: 'tenant_identity',
      statusLabel: '사용 가능',
      detailNotice: 'Knox SSO 사번과 서비스 권한을 기준으로 자동 적용됩니다.',
      hideConnectActions: true,
      hideVerifyAction: true,
    },
  },
  {
    id: 'samsung_knox',
    label: '삼성중공업 Knox',
    category: 'productivity',
    meta: {
      logoSrc: '',
      logoAlt: 'Samsung Knox',
      accentColor: '#0055FF',
      accentBackground: 'rgba(0, 85, 255, 0.08)',
    },
    catalog: {
      categoryKey: null,
      featuresKey: null,
      descriptionKey: 'pluginDetail.descriptions.samsungKnox',
      developer: '삼성중공업',
      websiteUrl: null,
      privacyPolicyUrl: null,
    },
    tools: [],
    ux: {
      connectionMode: 'tenant_identity',
      statusLabel: '사용 가능',
      // The PLAN 과 달리 서비스 권한(AX/DX)에 좌우되지 않는다 — Knox
      // 그룹웨어는 전사 공통이라 사번만 있으면 쓸 수 있다.
      detailNotice: 'Knox SSO 사번을 기준으로 자동 적용됩니다.',
      hideConnectActions: true,
      hideVerifyAction: true,
    },
  },
];

/** O(1) lookup map keyed by plugin id. */
export const BUILTIN_PLUGIN_MAP: Readonly<Record<PluginId, PluginDescriptor>> =
  Object.freeze(
    BUILTIN_PLUGINS.reduce<Record<PluginId, PluginDescriptor>>((acc, plugin) => {
      acc[plugin.id] = plugin;
      return acc;
    }, {}),
  );

/** Ordered list of known builtin plugin ids, for convenient iteration. */
export const BUILTIN_PLUGIN_IDS: readonly PluginId[] = BUILTIN_PLUGINS.map(
  (plugin) => plugin.id,
);

/** Type guard asserting a string matches a builtin plugin id. */
export function isBuiltinPluginId(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(BUILTIN_PLUGIN_MAP, id);
}
